"use client";

import { useState, ReactNode } from "react";
import { Button, Modal } from "rizzui";
import { useVendorButtonColors } from "@/contexts/vendor-button-colors-context";
import { PiX } from "react-icons/pi";
import logger from "@/utils/logger";

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title?: string;
  message?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info";
  isLoading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "info",
  isLoading = false,
}) => {
  const [loading, setLoading] = useState(false);
  const { addButtonColor, cancelButtonColor } = useVendorButtonColors();

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } catch (error) {
      logger.error("Error in confirm action:", error);
    } finally {
      setLoading(false);
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case "danger":
        return {
          confirmButtonBg: "#EF4444",
          cancelButtonBg: cancelButtonColor,
        };
      case "warning":
        return {
          confirmButtonBg: "#F59E0B",
          cancelButtonBg: cancelButtonColor,
        };
      default:
        return {
          confirmButtonBg: addButtonColor,
          cancelButtonBg: cancelButtonColor,
        };
    }
  };

  const variantStyles = getVariantStyles();
  const isDisabled = loading || isLoading;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="flex w-full flex-col overflow-hidden rounded-[inherit] bg-white text-left">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <h2 className="min-w-0 flex-1 text-base font-semibold leading-6 text-gray-900">
            {title}
          </h2>
          <button
            type="button"
            aria-label="Close dialog"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 disabled:hover:bg-transparent"
            disabled={isDisabled}
          >
            <PiX className="h-4 w-4" />
          </button>
        </div>

        {/* Message */}
        <div className="px-5 py-4">
          {typeof message === "string" ? (
            <p className="text-sm leading-relaxed text-gray-600">{message}</p>
          ) : (
            <div className="text-sm leading-relaxed text-gray-600">{message}</div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3.5">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isDisabled}
            className="h-9 px-4 text-sm font-medium"
            style={{
              color: variantStyles.cancelButtonBg,
              borderColor: variantStyles.cancelButtonBg,
            }}
          >
            {cancelText}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isDisabled}
            className="h-9 px-4 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 active:opacity-80"
            style={{
              backgroundColor: variantStyles.confirmButtonBg,
            }}
          >
            {isDisabled ? (
              <>
                <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Loading...
              </>
            ) : (
              confirmText
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDialog;
